/**
 * Popup Modal System for Occasions Fine Jewelry
 * Version: 2.1.5
 * Date: 2026-10-09
 * Author: Carmyn Wilson, Wizard of Ads
 *
 * Usage:
 * - Modal element: id="ofj-{modalname}-modal" class="ofj-modal"
 * - Inner dialog:  class="ofj-modal__inner"
 * - Open trigger:  data-ofj-modal-open="{modalname}"
 * - Close trigger: data-ofj-modal-close (inside the modal)
 *
 * Appointment type pre-selection:
 * - Add data-ofj-appointment-type="{id}" to any trigger
 * - The iframe src will be updated with ?appointmentType={id}
 * - The iframe src is (re)set on open — generic if no type is provided
 * - The iframes ship with no src, so the scheduler only loads when a modal opens
 * - It starts a moment early when someone points at, touches or tabs to an opener
 *
 * Legacy support:
 * - .book-appointment → opens booking modal
 */

(function () {
  'use strict';

  /**
   * Legacy class-to-modal mapping
   * Add entries here to support existing class-based triggers
   */
  var LEGACY_TRIGGERS = {
    'book-appointment': 'booking'
  };

  var activeModal = null;
  var lastFocused = null;
  var scrollbarWidth = 0;
  var lockedScrollY = 0;

  /**
   * Keep the window pinned to lockedScrollY while a modal is open.
   *
   * overflow:hidden stops the user from scrolling but NOT programmatic scrolls.
   * The Acuity iframe autofocuses a field a second or two after it loads, and
   * the browser scrolls the window to the top to reveal that focus. This snaps
   * it straight back — instantly, so the theme's smooth scroll-behavior can't
   * animate a visible jump.
   */
  function keepScrollPinned() {
    if (window.pageYOffset === lockedScrollY) return;
    var docEl = document.documentElement;
    var prevBehavior = docEl.style.scrollBehavior;
    docEl.style.scrollBehavior = 'auto';
    window.scrollTo(0, lockedScrollY);
    docEl.style.scrollBehavior = prevBehavior;
  }

  /**
   * Get all focusable elements within a container
   *
   * The scheduler iframe counts as a stop: without it, Tab never reached the
   * Acuity form, and Close was both the first and the last stop. The focus
   * wrap (see ensureFocusWrap) is left out, so it never becomes "last".
   * @param {HTMLElement} container
   * @returns {NodeList}
   */
  function getFocusableElements(container) {
    return container.querySelectorAll(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), iframe:not([tabindex="-1"]), [tabindex]:not([tabindex="-1"]):not([data-ofj-focus-wrap])'
    );
  }

  /**
   * Keep Tab inside a dialog whose last stop is an iframe.
   *
   * Key presses inside the Acuity iframe happen in Acuity's page, so
   * handleKeydown never sees them: Tab past the scheduler's last field would
   * carry focus out of the dialog to whatever follows it in the page. A
   * focusable, empty stop after the dialog's content catches that focus and
   * sends it to the first stop (Close). Escape pressed inside the iframe is
   * Acuity's too; it closes the modal once focus is back on Close.
   * @param {HTMLElement} dialog
   */
  function ensureFocusWrap(dialog) {
    if (!dialog || !dialog.querySelector('iframe') || dialog.querySelector('[data-ofj-focus-wrap]')) return;

    var wrap = document.createElement('span');
    wrap.setAttribute('tabindex', '0');
    wrap.setAttribute('data-ofj-focus-wrap', '');
    wrap.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;';
    wrap.addEventListener('focus', function () {
      var focusables = getFocusableElements(dialog);
      if (focusables.length) focusables[0].focus({ preventScroll: true });
    });
    dialog.appendChild(wrap);
  }

  /**
   * Check if an element matches any legacy trigger class
   * @param {HTMLElement} element
   * @returns {string|null} Modal name or null
   */
  function checkLegacyTrigger(element) {
    for (var className in LEGACY_TRIGGERS) {
      if (element.closest('.' + className)) {
        return LEGACY_TRIGGERS[className];
      }
    }
    return null;
  }

  /**
   * The element a legacy class-based trigger matched, so focus can go back to it.
   * @param {HTMLElement} element
   * @returns {HTMLElement|null}
   */
  function legacyTriggerElement(element) {
    return element.closest(Object.keys(LEGACY_TRIGGERS).map(function (className) {
      return '.' + className;
    }).join(', '));
  }

  /**
   * Hand a freshly loaded scheduler iframe to Acuity's embed.js.
   *
   * The booking iframes ship with no src (each scheduler is about 3 MB of
   * Squarespace, reCAPTCHA and Stripe, and the modals are on every page).
   * embed.js, loaded in the header, only registers iframes that already have
   * an Acuity src when it runs, so it never sees ours, and without it the
   * frame loses its auto-height, the page's URL parameters (first_name,
   * email, appointmentType...) and the analytics hand-off. Running it again
   * after the src is set registers the frame. embed.js guards against
   * registering a frame twice or binding its listener twice, and the browser
   * has it cached. Only done when the page already loads embed.js.
   * @param {HTMLIFrameElement} iframe
   */
  function registerWithAcuityEmbed(iframe) {
    if (!iframe || iframe.__acuityInited || iframe.__ofjEmbedRequested) return;
    var embed = document.querySelector('script[src*="acuityscheduling.com/js/embed.js"]');
    if (!embed) return;

    iframe.__ofjEmbedRequested = true;
    var script = document.createElement('script');
    script.src = embed.src;
    script.async = true;
    document.head.appendChild(script);
  }

  /**
   * Update the iframe src inside a modal with an appointmentType parameter.
   * Reads the base src from data-base-src on the iframe.
   * @param {HTMLElement} modal
   * @param {string} appointmentType
   */
  function setAppointmentType(modal, appointmentType) {
    var iframe = modal.querySelector('iframe[data-base-src]');
    if (!iframe) return;

    var baseSrc = iframe.getAttribute('data-base-src');
    var url = new URL(baseSrc);
    url.searchParams.set('appointmentType', appointmentType);
    iframe.src = url.toString();
  }

  /**
   * Build the iframe src for this open: appointment type plus any Acuity
   * intake-field prefill, applied in ONE pass.
   *
   * Both used to be separate setters, and the second would have rebuilt the
   * src from data-base-src and dropped the first one's parameter. Everything
   * is layered onto a single URL here instead.
   *
   * Acuity prefills a custom intake field with `field:<fieldId>=<value>`.
   * The field id comes from Acuity admin — the public embed JSON exposes form
   * ids but not field ids.
   *
   * @param {HTMLElement} modal
   * @param {Object} options
   * @param {string} [options.appointmentType]
   * @param {string} [options.prefillField] - Acuity intake field id
   * @param {string} [options.prefillValue] - value to drop into that field
   */
  function applyIframeParams(modal, options) {
    var iframe = modal.querySelector('iframe[data-base-src]');
    if (!iframe) return;

    iframe.src = buildIframeSrc(iframe, options);
    registerWithAcuityEmbed(iframe);
  }

  /**
   * The src applyIframeParams sets, without setting it.
   * @param {HTMLIFrameElement} iframe
   * @param {Object} options - as for applyIframeParams
   * @returns {string}
   */
  function buildIframeSrc(iframe, options) {
    var url = new URL(iframe.getAttribute('data-base-src'));

    if (options.appointmentType) {
      url.searchParams.set('appointmentType', options.appointmentType);
    }

    if (options.prefillField && options.prefillValue) {
      // Two spellings on purpose. `field:<id>` is Acuity's long-documented
      // scheduling-URL prefill; `fields[field-<id>]` is what the current
      // scheduler names the input in its own markup. Acuity ignores a
      // parameter it doesn't recognise, so sending both costs nothing and
      // means this works whichever one the embed is honouring. Once it's
      // confirmed on the live form, drop the other.
      // searchParams handles the encoding, including the colon and brackets.
      url.searchParams.set('field:' + options.prefillField, options.prefillValue);
      url.searchParams.set('fields[field-' + options.prefillField + ']', options.prefillValue);
    }

    return url.toString();
  }

  /**
   * Reset the iframe src inside a modal back to its data-base-src value.
   * @param {HTMLElement} modal
   */
  function resetIframeSrc(modal) {
    var iframe = modal.querySelector('iframe[data-base-src]');
    if (!iframe) return;

    var baseSrc = iframe.getAttribute('data-base-src');
    if (baseSrc) {
      iframe.src = baseSrc;
      registerWithAcuityEmbed(iframe);
    }
  }

  /**
   * Whether an open with these options sets an appointment type or prefill
   * (applyIframeParams) rather than the generic scheduler (resetIframeSrc).
   * @param {Object} options
   * @returns {boolean}
   */
  function hasIframeParams(options) {
    return !!(options.appointmentType || (options.prefillField && options.prefillValue));
  }

  /**
   * Start a modal's scheduler before the click.
   *
   * With no src on the iframes, the first open has to fetch the whole
   * scheduler, which on a slow phone is a long blank wait. The first time
   * someone shows intent on an opener (the pointer reaches it, a finger lands
   * on it, or keyboard focus arrives), its modal's iframe gets the src that
   * opener's click will ask for, and the open keeps that load instead of
   * starting over. Once per modal per page, only while the iframe has no src:
   * nothing loads for a visitor who never goes near a booking button.
   *
   * No preconnect: the scheduler runs in its own frame, and Chromium keeps a
   * cross-site frame's connections apart from the page's, so sockets the page
   * opened to Acuity or Squarespace went unused when measured.
   * @param {Event} e - pointerover, touchstart or focusin
   */
  function warmUp(e) {
    var target = e.target;
    if (!target || typeof target.closest !== 'function') return;

    var opener = target.closest('[data-ofj-modal-open]');
    var modalName = opener ? opener.getAttribute('data-ofj-modal-open') : checkLegacyTrigger(target);
    if (!modalName) return;

    var modal = document.getElementById('ofj-' + modalName + '-modal');
    var iframe = modal && modal.querySelector('iframe[data-base-src]');
    if (!iframe || iframe.__ofjWarmed || iframe.getAttribute('src')) return;

    // The same attributes the click handler reads.
    var options = opener ? {
      appointmentType: opener.getAttribute('data-ofj-appointment-type') || null,
      prefillField: opener.getAttribute('data-ofj-prefill-field') || null,
      prefillValue: opener.getAttribute('data-ofj-prefill-value') || null
    } : {};

    iframe.__ofjWarmed = true;
    iframe.__ofjWarmSrc = hasIframeParams(options) ? buildIframeSrc(iframe, options) : iframe.getAttribute('data-base-src');
    iframe.src = iframe.__ofjWarmSrc;
    registerWithAcuityEmbed(iframe);
  }

  /**
   * On an open, keep the warm-up's load if it is the scheduler this open
   * wants. Only the first open can: later opens start the scheduler over, as
   * they always have, so a half-finished booking never reappears.
   * @param {HTMLElement} modal
   * @param {Object} options - the open's options
   * @returns {boolean} true when the iframe already has the right load
   */
  function keepWarmLoad(modal, options) {
    var iframe = modal.querySelector('iframe[data-base-src]');
    if (!iframe || !iframe.__ofjWarmSrc) return false;

    var warmSrc = iframe.__ofjWarmSrc;
    iframe.__ofjWarmSrc = null;
    var wanted = hasIframeParams(options) ? buildIframeSrc(iframe, options) : iframe.getAttribute('data-base-src');
    return warmSrc === wanted;
  }

  /**
   * Open a modal by name
   * @param {string} modalName - The name portion of the modal ID (e.g., "booking" for "ofj-booking-modal")
   * @param {Object} [options] - Optional settings
   * @param {string} [options.appointmentType] - Acuity appointment type ID to pre-select
   * @param {HTMLElement} [options.trigger] - What opened it; focus goes back here on close (default: whatever had focus)
   */
  function openModal(modalName, options) {
    options = options || {};

    var modal = document.getElementById('ofj-' + modalName + '-modal');
    if (!modal) {
      console.warn('OFJ Modal: No modal found with name "' + modalName + '"');
      return;
    }

    // Close any currently open modal first
    if (activeModal) {
      closeModal(false); // Don't restore focus yet
    }

    // Store the element that triggered the modal. The click handler passes the
    // opener itself: Safari doesn't focus a button on click, so activeElement
    // would be the page and focus would have nowhere to go back to.
    lastFocused = options.trigger || document.activeElement;
    activeModal = modal;

    // Set the iframe src for this open: pre-select an appointment type if one
    // was provided, otherwise reset to the generic scheduler. Doing this on
    // open (rather than on close) means closing never triggers an async iframe
    // reload that could yank the scroll position a moment later.
    // A warm-up (see warmUp) may already have started the right scheduler.
    if (keepWarmLoad(modal, options)) {
      // Keep it.
    } else if (hasIframeParams(options)) {
      applyIframeParams(modal, options);
    } else {
      resetIframeSrc(modal);
    }

    // Show the modal
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('ofj-modal--open');

    // Lock background scroll WITHOUT moving the page. A position:fixed lock
    // breaks the theme's sticky header (its containing block changes, so it
    // scrolls off) and forces a scroll-restore on close. Locking overflow on
    // <html> leaves the header and scroll position exactly where they are.
    // Pad for the now-missing scrollbar so content doesn't shift sideways.
    var docEl = document.documentElement;
    lockedScrollY = window.pageYOffset;
    scrollbarWidth = window.innerWidth - docEl.clientWidth;
    docEl.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      docEl.style.paddingRight = scrollbarWidth + 'px';
    }

    // Catch programmatic scrolls (e.g. the Acuity iframe's autofocus) and snap
    // the window back to where it was when the modal opened.
    window.addEventListener('scroll', keepScrollPinned, { passive: true });

    // Focus the dialog or first focusable element
    var dialog = modal.querySelector('.ofj-modal__inner');
    ensureFocusWrap(dialog);
    var focusables = getFocusableElements(dialog);

    if (focusables.length) {
      focusables[0].focus({ preventScroll: true });
    } else if (dialog) {
      dialog.focus({ preventScroll: true });
    }

    // Add keyboard listener
    document.addEventListener('keydown', handleKeydown);

    // Dispatch custom event
    modal.dispatchEvent(new CustomEvent('ofj-modal:opened', {
      bubbles: true,
      detail: { modalName: modalName, appointmentType: options.appointmentType || null }
    }));

    // Repeat it on Shopify's customer-events bus. Pixels added under
    // Settings > Customer events run in a sandbox and can't hear DOM events,
    // so this is the only way they can see a modal open. Guarded so tracking
    // can never stop a modal from opening.
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish('ofj:modal_opened', {
          modalName: modalName,
          appointmentType: options.appointmentType || null
        });
      }
    } catch (err) {
      // Ignore: analytics is optional.
    }
  }

  /**
   * Close the currently active modal
   * @param {boolean} restoreFocus - Whether to restore focus to the trigger element
   */
  function closeModal(restoreFocus) {
    if (restoreFocus === undefined) restoreFocus = true;
    if (!activeModal) return;

    var modalName = activeModal.id.replace('ofj-', '').replace('-modal', '');
    var modalEl = activeModal;

    // Stop pinning the window before we do anything else, so it can't fight
    // the scroll restore below.
    window.removeEventListener('scroll', keepScrollPinned);

    // Remember where we are — used as a safety net below.
    var restoreY = window.pageYOffset;

    // Move focus OUT of the modal BEFORE hiding it. If a focused element inside
    // the modal — most importantly the Acuity <iframe> — is hidden while it
    // still holds focus, the browser scrolls the page to the top. Restoring
    // focus to the trigger first (with preventScroll) leaves the scroll alone.
    if (restoreFocus && lastFocused && typeof lastFocused.focus === 'function') {
      try {
        lastFocused.focus({ preventScroll: true });
      } catch (err) {
        lastFocused.focus();
      }
    }
    if (document.activeElement && modalEl.contains(document.activeElement)) {
      // No trigger to restore to (e.g. reopening), or it couldn't take focus
      // (hidden, or an <a> with no href): just drop focus safely.
      document.activeElement.blur();
    }
    lastFocused = null;

    // Hide the modal
    modalEl.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('ofj-modal--open');

    // Release the scroll lock. We never moved the page, so there is nothing to
    // restore — no scrollTo, no snap-to-top, no smooth-scroll drift.
    var docEl = document.documentElement;
    docEl.style.overflow = '';
    docEl.style.paddingRight = '';

    // Safety net: if anything (a stray focus/scrollIntoView) still moved the
    // page, snap it back instantly — bypassing the theme's smooth scroll so it
    // can't animate a visible jump.
    if (window.pageYOffset !== restoreY) {
      var prevBehavior = docEl.style.scrollBehavior;
      docEl.style.scrollBehavior = 'auto';
      window.scrollTo(0, restoreY);
      docEl.style.scrollBehavior = prevBehavior;
    }

    // Remove keyboard listener
    document.removeEventListener('keydown', handleKeydown);

    // Dispatch custom event
    modalEl.dispatchEvent(new CustomEvent('ofj-modal:closed', {
      bubbles: true,
      detail: { modalName: modalName }
    }));

    activeModal = null;
  }

  /**
   * Keep the keyup of the Escape that closed a modal away from the page.
   *
   * The modal closes on keydown and focus goes straight back to the opener,
   * so the key's keyup lands on the opener. When that is the header's Book
   * Appointment, the theme's header drawer takes it as "Escape the menu" and
   * moves focus to the hamburger (Show menu). Stopping that one keyup at the
   * window, before anything else hears it, leaves every later Escape alone.
   */
  function swallowEscapeKeyup() {
    var timer;
    function onKeyup(e) {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      done();
    }
    function done() {
      window.removeEventListener('keyup', onKeyup, true);
      clearTimeout(timer);
    }
    window.addEventListener('keyup', onKeyup, true);
    // If the keyup never comes here (the window lost focus), don't eat a later one.
    timer = setTimeout(done, 1000);
  }

  /**
   * Handle keyboard events for accessibility
   * @param {KeyboardEvent} e
   */
  function handleKeydown(e) {
    if (!activeModal) return;

    // Close on Escape
    if (e.key === 'Escape') {
      e.preventDefault();
      swallowEscapeKeyup();
      closeModal();
      return;
    }

    // Trap focus on Tab
    if (e.key === 'Tab') {
      var dialog = activeModal.querySelector('.ofj-modal__inner');
      var focusables = Array.from(getFocusableElements(dialog));

      if (!focusables.length) return;

      var first = focusables[0];
      var last = focusables[focusables.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  /**
   * Auto-promote known store-address maps links into directions-modal triggers.
   *
   * The footer text block uses Shopify's richtext field, which strips
   * data-* attributes — so we can't paste `data-ofj-modal-open="directions"`
   * directly into the rich text editor. Instead, anyone editing the rich
   * text just adds a normal link to one of the OFJ store maps URLs below,
   * and this function rewrites those anchors into modal triggers.
   *
   * NOTE for multi-location: when we add a second store, switch this to a
   * per-anchor `data-ofj-directions-address` pattern (mirrors VX's
   * inc/maps.php + assets/js/maps.js) so each link can drive a different
   * destination address.
   */
  var STORE_MAPS_URLS = [
    'maps.app.goo.gl/zZ8YGzv2dAugsThd6',
    'maps.app.goo.gl/vcafPz9vP9r1bcE17'
  ];

  function promoteMapsLinks() {
    var anchors = document.querySelectorAll('a[href]');
    for (var i = 0; i < anchors.length; i++) {
      var a = anchors[i];
      var href = a.getAttribute('href') || '';
      var matched = false;
      for (var j = 0; j < STORE_MAPS_URLS.length; j++) {
        if (href.indexOf(STORE_MAPS_URLS[j]) !== -1) {
          matched = true;
          break;
        }
      }
      if (!matched) continue;

      a.setAttribute('href', '#');
      a.setAttribute('data-ofj-modal-open', 'directions');
      a.removeAttribute('target');
      a.removeAttribute('rel');
      a.removeAttribute('title');
    }
  }

  /**
   * Initialize click handlers via event delegation
   */
  function init() {
    promoteMapsLinks();

    ['pointerover', 'touchstart', 'focusin'].forEach(function (type) {
      document.addEventListener(type, warmUp, { passive: true });
    });

    document.addEventListener('click', function (e) {
      // Check for data attribute trigger (preferred)
      var openTrigger = e.target.closest('[data-ofj-modal-open]');
      if (openTrigger) {
        e.preventDefault();
        var modalName = openTrigger.getAttribute('data-ofj-modal-open');
        openModal(modalName, {
          appointmentType: openTrigger.getAttribute('data-ofj-appointment-type') || null,
          prefillField: openTrigger.getAttribute('data-ofj-prefill-field') || null,
          prefillValue: openTrigger.getAttribute('data-ofj-prefill-value') || null,
          trigger: openTrigger
        });
        return;
      }

      // Check for legacy class-based triggers
      var legacyModalName = checkLegacyTrigger(e.target);
      if (legacyModalName) {
        e.preventDefault();
        openModal(legacyModalName, { trigger: legacyTriggerElement(e.target) });
        return;
      }

      // Check for close trigger (must be inside a modal)
      var closeTrigger = e.target.closest('[data-ofj-modal-close]');
      if (closeTrigger) {
        e.preventDefault();
        closeModal();
        return;
      }
    }, { passive: false });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose API globally for programmatic control
  window.OFJModal = {
    open: openModal,
    close: closeModal
  };

})();